import { useGeolocation } from '@uidotdev/usehooks';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { closeAllModals } from '../../../../actions/modalActions';
import useLocations from '../../../../hooks/useLocations';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import { A } from '../../../base/typography';
import { LocationsMapContainer } from '../../../map/mapContainer';
import * as StyledModals from '../myShipmentsModals.styles';
import DeliveryOptionsList from './deliveryOptionsList';

interface Props {}

const DeliveryOption: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const { locations } = useLocations();
	const RVK_LAT = 64.1256369;
	const RVK_LNG = -21.8756546;
	const ICELAND_CENTER_LAT = 64.9386083;
	const ICELAND_CENTER_LNG = -18.8422143;
	const geolocation = useGeolocation();
	const [latitude, setLatitude]: [number, any] = useState(geolocation.latitude || RVK_LAT);
	const [longitude, setLongitude]: [number, any] = useState(geolocation.longitude || RVK_LNG);
	const [zoom, setZoom]: [number, any] = useState(12);
	const [selectedLocation, setSelectedLocation]: [string, any] = useState(null);
	const { modalHeading } = useSelector(({ modalReducer }) => modalReducer);
	const [searchQuery, setSearchQuery] = useState(modalHeading);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { modalSubmitError, modalSubmitErrorDetails } = userProfileReducer;

	const onMarkerClick = (locationId, lat, lng, scroll) => {
		if (scroll) document.getElementById(locationId).scrollIntoView();
		setSelectedLocation(locationId);
		setLatitude(lat);
		setLongitude(lng);
		setZoom(16);
	};

	const onChange = e => {
		setSearchQuery(e.target.value);
		setZoom(7);
		setLatitude(ICELAND_CENTER_LAT);
		setLongitude(ICELAND_CENTER_LNG);
	};

	const mapLocationTypeToIcelandic = type => {
		if (type === 'ParcelPoint') return 'pakkaport';
		if (type === 'PostBox') return 'póstbox';
		if (type === 'PostOffice') return 'pósthús';
		return type;
	};

	const filteredLocations = useMemo(() => {
		// Filter out locations with a null name
		return locations.filter(location => location.name !== null);
	}, [locations]);

	const hasSearchError = filteredLocations.length === 0;

	const search = location => {
		const upperSearchQuery = searchQuery.toUpperCase();
		return (
			searchQuery.length === 0 ||
			(location.name && location.name.toUpperCase().includes(upperSearchQuery)) ||
			(location.address &&
				location.address.postalCode &&
				location.address.postalCode.toUpperCase().includes(upperSearchQuery)) ||
			(location.address &&
				location.address.street1 &&
				location.address.street1.toUpperCase().includes(upperSearchQuery)) ||
			(location.locationType && location.locationType.toUpperCase().includes(upperSearchQuery)) ||
			(location.locationType &&
				mapLocationTypeToIcelandic(location.locationType).toUpperCase().includes(upperSearchQuery))
		);
	};

	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};

	document.body.style.overflow = 'hidden';

	return (
		<StyledModals.Wrapper large>
			<StyledModals.FullHeightColumns>
				<StyledModals.DeliveryOptionsWrapper desktopSmall={5 / 12}>
					<div>
						<StyledModals.Heading>
							<div>{t('page-settings.chooseDeliveryOption')}</div>
							<A onClick={closeModal}>
								<Close />
							</A>
						</StyledModals.Heading>
					</div>
					<StyledModals.InputWrapper>
						<StyledModals.SearchInputWrapper>
							<StyledModals.SearchInput
								type="text"
								value={searchQuery}
								onChange={onChange}
								placeholder={`${t('page-settings.deliveryOptionSearch')}`}
							/>
							<FaSearch />
						</StyledModals.SearchInputWrapper>
					</StyledModals.InputWrapper>
					{modalSubmitError && (
						<StyledModals.ErrorMessage>
							<FaExclamationCircle />
							<div>{modalSubmitErrorDetails !== '' ? modalSubmitErrorDetails : t('genericError')}</div>
						</StyledModals.ErrorMessage>
					)}
					<DeliveryOptionsList
						search={search}
						locations={locations}
						selectedLocation={selectedLocation}
						setSelectedLocation={onMarkerClick}
					/>
					{hasSearchError && (
						<StyledModals.ErrorMessage>
							<FaExclamationCircle />
							<div>{t('genericError')}</div>
						</StyledModals.ErrorMessage>
					)}
				</StyledModals.DeliveryOptionsWrapper>
				<StyledModals.StyledColumn desktopSmall={7 / 12}>
					<StyledModals.MapWrapper>
						<LocationsMapContainer
							lat={latitude}
							lng={longitude}
							locations={locations}
							zoom={zoom}
							onMarkerClick={onMarkerClick}
							search={search}
						/>
					</StyledModals.MapWrapper>
				</StyledModals.StyledColumn>
			</StyledModals.FullHeightColumns>
		</StyledModals.Wrapper>
	);
};

export default DeliveryOption;
